/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState, useCallback } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {
  Card,
  CardContent,
  useMediaQuery,
  Alert,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab,
  Box,
  DialogContentText,
  Avatar,
  Link,
  Badge,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { GET, PUT, POST, BASE_URL } from "../../APIclient";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
import properties from "constants/ApplicationConstants";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from "@mui/icons-material/Close";
import BugReportIcon from '@mui/icons-material/BugReport';
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RemoveIcon from "@mui/icons-material/Remove";
import SortIcon from "@mui/icons-material/Sort";
import CommentIcon from "@mui/icons-material/Comment";
import SendIcon from "@mui/icons-material/Send";
import HistoryIcon from "@mui/icons-material/History";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import axios from "axios";

const EmployeeAssetsHistory = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [ticketFormData, setTicketFormData] = useState({
    title: "",
    issueDescription: "",
    priority: "MEDIUM",
    image: null,
  });
  const [ticketLoading, setTicketLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [assetTickets, setAssetTickets] = useState([]);
  const [dialogTab, setDialogTab] = useState(0);
  const [reopenConfirm, setReopenConfirm] = useState({ open: false, ticket: null });
  const [reopenReason, setReopenReason] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [ticketDetailsOpen, setTicketDetailsOpen] = useState(false);
  const [selectedTicketForDetails, setSelectedTicketForDetails] = useState(null);
  const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);
  const [selectedTicketForComments, setSelectedTicketForComments] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [commentLoading, setCommentLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState("newest");
  const [employeeNamesCache, setEmployeeNamesCache] = useState({});
  const [unreadCommentCount, setUnreadCommentCount] = useState(0);
  const [lastReadCommentId, setLastReadCommentId] = useState(null);
  const [commentsReadRefresh, setCommentsReadRefresh] = useState(0);

  const empId = window.sessionStorage.getItem("empId");
  const empName = window.sessionStorage.getItem("EmpName") || "";

  // Fetch tickets function - extracted to useCallback for polling
  const fetchAssetsAndTickets = useCallback(async (showLoading = true) => {
    if (!empId) return;
    
    if (showLoading) {
      setLoading(true);
    }
    
    try {
      const response = await GET(`it-dept/workstation/view/${empId}`);
      if (response.data?.statusCode === 200) {
        const allAssets = response.data.data || [];

        const sortedAssets = allAssets.sort((a, b) => {
          const aReturned = !!a.returnedDate;
          const bReturned = !!b.returnedDate;
          return aReturned - bReturned;
        });

        setAssets(sortedAssets);

        // Get currently assigned assets (not returned) and their assetIds
        const currentlyAssignedAssets = sortedAssets.filter(asset => !asset.returnedDate);
        const assignedAssetIds = currentlyAssignedAssets.map(asset => asset.assetId).filter(id => id);
        
        // Fetch all tickets for this employee in a single API call
        try {
          const res = await GET(`it-dept/tickets?empId=${empId}`);
          if (res?.data?.statusCode === 200) {
            const allTickets = res.data.data?.paginatedData || [];
            
            // Filter tickets to only include those for currently assigned assets
            const filteredTickets = assignedAssetIds.length > 0
              ? allTickets.filter(ticket => assignedAssetIds.includes(ticket.assetId))
              : allTickets;
            
            // Sort by created date (newest first)
            const sorted = filteredTickets.sort((a, b) => {
              const ad = a?.createdOn ? new Date(a.createdOn).getTime() : 0;
              const bd = b?.createdOn ? new Date(b.createdOn).getTime() : 0;
              return bd - ad;
            });
            
            setTickets(sorted);
          } else {
            setTickets([]);
          }
        } catch (e) {
          console.error("Error fetching tickets:", e);
          setTickets([]);
        }
      } else {
        if (showLoading) {
          setError("Failed to fetch asset data.");
        }
      }
    } catch (err) {
      if (showLoading) {
        setError("Error fetching assets: " + err.message);
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [empId]);

  // Initial fetch on mount
  useEffect(() => {
    if (!empId) {
      setError("No employee ID found. Please log in.");
      setLoading(false);
      return;
    }

    fetchAssetsAndTickets(true);
  }, [empId, fetchAssetsAndTickets]);

  // Poll tickets every 5 seconds to get priority updates from IT admin
  useEffect(() => {
    if (!empId) return;

    const pollTickets = () => {
      fetchAssetsAndTickets(false); // Don't show loading spinner on polling
    };

    // Poll every 5 seconds
    const interval = setInterval(pollTickets, 5000);

    return () => clearInterval(interval);
  }, [empId, fetchAssetsAndTickets]);

  // Update comments when employee names are fetched
  useEffect(() => {
    if (selectedTicketForDetails && comments.length > 0) {
      // Trigger re-render when names cache updates
      const parsedComments = parseComments(selectedTicketForDetails.commentsJson);
      if (parsedComments.length === comments.length) {
        // Only update if we have the same number of comments (to avoid infinite loop)
        setComments([...parsedComments]);
      }
    }
  }, [employeeNamesCache]);

  // Real-time comment polling when comments dialog is open
  useEffect(() => {
    if (!commentsDialogOpen || !selectedTicketForComments) return;

    let lastCommentCount = comments.length;

    const pollComments = async () => {
      try {
        const res = await GET(`it-dept/tickets?empId=${empId}`);
        if (res?.data?.statusCode === 200) {
          const list = res?.data?.data?.paginatedData || [];
          const updated = list.find(t => t.ticketId === selectedTicketForComments.ticketId);
          if (updated) {
            const parsedComments = parseComments(updated.commentsJson);
            // Only update if comments have changed
            if (parsedComments.length !== lastCommentCount) {
              setComments(parsedComments);
              setSelectedTicketForComments(updated);
              lastCommentCount = parsedComments.length;
              
              // User is viewing comments - mark all new comments as read
              const readCommentsKey = `readComments_${selectedTicketForComments.ticketId}_${empId}`;
              let readComments = JSON.parse(localStorage.getItem(readCommentsKey) || '[]');
              const newCommentTimestamps = parsedComments
                .filter(c => String(c.senderId) !== String(empId))
                .map(c => c.timestamp);
              const allRead = [...new Set([...readComments, ...newCommentTimestamps])];
              localStorage.setItem(readCommentsKey, JSON.stringify(allRead));
              setUnreadCommentCount(0);
              
              if (parsedComments.length > 0) {
                setLastReadCommentId(parsedComments[parsedComments.length - 1].timestamp);
              }
            }
          }
        }
      } catch (err) {
        console.error("Error polling comments:", err);
      }
    };

    // Poll immediately, then every 3 seconds
    pollComments();
    const interval = setInterval(pollComments, 3000);

    return () => clearInterval(interval);
  }, [commentsDialogOpen, selectedTicketForComments?.ticketId, empId]);

  const handleOpenTicketDialog = (asset) => {
    setSelectedAsset(asset);
    setTicketFormData({
      title: "",
      issueDescription: "",
      priority: "MEDIUM",
      image: null,
    });
    setTicketDialogOpen(true);
    // Filter existing tickets for this asset
    const assetId = asset?.assetId;
    const filtered = tickets.filter(t => String(t.empId) === String(empId) && (String(t.assetId) === String(assetId)));
    setAssetTickets(filtered);
    setDialogTab(filtered.length > 0 ? 1 : 0);
  };

  const handleCloseTicketDialog = () => {
    setTicketDialogOpen(false);
    setSelectedAsset(null);
    setTicketFormData({
      title: "",
      issueDescription: "",
      priority: "MEDIUM",
      image: null,
    });
  };

  const handleTicketFormChange = (field, value) => {
    setTicketFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setTicketFormData((prev) => ({
        ...prev,
        image: file,
      }));
    }
  };

  
  const handleSubmitTicket = async () => {
    if (!ticketFormData.title.trim()) {
      setSnackbar({
        open: true,
        message: "Please enter a title for the ticket",
        severity: "error",
      });
      return;
    }

    if (!ticketFormData.issueDescription.trim()) {
      setSnackbar({
        open: true,
        message: "Please enter an issue description",
        severity: "error",
      });
      return;
    }

    if (!selectedAsset) {
      setSnackbar({
        open: true,
        message: "No asset selected",
        severity: "error",
      });
      return;
    }

    setTicketLoading(true);

    const ticketPayload = {
      empId: parseInt(empId),
      empName: empName,
      assetId: selectedAsset.assetId,
      title: ticketFormData.title.trim(),
      issueDescription: ticketFormData.issueDescription.trim(),
      status: "OPEN",
      priority: ticketFormData.priority,
    };

    
    const formData = new FormData();
    formData.append("ticket", new File(
      [JSON.stringify(ticketPayload, null, 2)],
      "ticket.json",
      { type: "application/json" }
    ));

    if (ticketFormData.image) {
      formData.append("image", ticketFormData.image);
    }

    try {
      const response = await axios.post(`${BASE_URL}/it-dept/tickets/add`, formData, {
        headers: {
          // "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${window.sessionStorage.getItem("jwtToken")}`,
        },
      });

      if (response.data?.statusCode === 201) {
        setSnackbar({
          open: true,
          message: response.data.message || "IT Ticket created successfully",
          severity: "success",
        });
        
        // Refresh tickets list to include the newly created ticket
        try {
          const res = await GET(`it-dept/tickets?empId=${empId}`);
          if (res?.data?.statusCode === 200) {
            const list = res?.data?.data?.paginatedData || [];
            const sorted = (Array.isArray(list) ? list : []).slice().sort((a,b) => {
              const ad = a?.createdOn ? new Date(a.createdOn).getTime() : 0;
              const bd = b?.createdOn ? new Date(b.createdOn).getTime() : 0;
              return bd - ad;
            });
            setTickets(sorted);
            
            // Update assetTickets if dialog is still open
            if (selectedAsset) {
              const assetId = selectedAsset?.assetId;
              const filtered = sorted.filter(t => String(t.empId) === String(empId) && (String(t.assetId) === String(assetId)));
              setAssetTickets(filtered);
              // Switch to Existing Tickets tab to show the new ticket
              setDialogTab(1);
            }
          }
        } catch (fetchError) {
          console.error("Error refreshing tickets:", fetchError);
        }
        
        // Reset form
        setTicketFormData({
          title: "",
          issueDescription: "",
          priority: "MEDIUM",
          image: null,
        });
      } else {
        setSnackbar({
          open: true,
          message: response.data?.message || "Failed to create ticket",
          severity: "error",
        });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "An error occurred. Please try again.",
        severity: "error",
      });
    } finally {
      setTicketLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getAvatarColor = (name) => {
    if (!name) return "#1976d2";
    const colors = [
      "#1976d2", "#388e3c", "#f57c00", "#7b1fa2", "#c2185b",
      "#0288d1", "#00796b", "#5d4037", "#455a64", "#d32f2f"
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const parseComments = (commentsJson) => {
    if (!commentsJson) return [];
    try {
      const parsed = typeof commentsJson === 'string' ? JSON.parse(commentsJson) : commentsJson;
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error("Error parsing comments:", e);
      return [];
    }
  };

  const handleViewTicketDetails = async (ticket) => {
    setSelectedTicketForDetails(ticket);
    setTicketDetailsOpen(true);
  };

  const handleOpenComments = async (ticket) => {
    setSelectedTicketForComments(ticket);
    const parsedComments = parseComments(ticket.commentsJson);
    setComments(parsedComments);
    setCommentsDialogOpen(true);
    setSortOrder("newest"); // Default sort order for comments
    
    // Mark all comments as read when opening the comments dialog
    const readCommentsKey = `readComments_${ticket.ticketId}_${empId}`;
    const readTimestamps = parsedComments
      .filter(c => String(c.senderId) !== String(empId))
      .map(c => c.timestamp);
    const existingRead = JSON.parse(localStorage.getItem(readCommentsKey) || '[]');
    const allRead = [...new Set([...existingRead, ...readTimestamps])];
    localStorage.setItem(readCommentsKey, JSON.stringify(allRead));
    setUnreadCommentCount(0); // Reset unread count for this ticket
    setCommentsReadRefresh(prev => prev + 1); // Trigger re-render to update badges
    if (parsedComments.length > 0) {
      setLastReadCommentId(parsedComments[parsedComments.length - 1].timestamp);
    }
    
    // Pre-fetch names for all comment senders
    if (parsedComments && parsedComments.length > 0) {
      const uniqueSenderIds = [...new Set(parsedComments.map(c => c.senderId))];
      const fetchPromises = uniqueSenderIds.map(async (senderId) => {
        if (!employeeNamesCache[senderId] && String(senderId) !== String(empId)) {
          const name = await getSenderName(senderId, ticket);
          return { senderId, name };
        }
        return null;
      });
      await Promise.all(fetchPromises);
      setComments([...parsedComments]); // Force re-render after names are fetched
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTicketForComments) return;
    if (newComment.trim().length > 150) {
      setSnackbar({
        open: true,
        message: "Comment cannot exceed 150 characters",
        severity: "error"
      });
      return;
    }

    setCommentLoading(true);
    try {
      const payload = {
        senderId: parseInt(empId),
        timestamp: new Date().toISOString(),
        content: newComment.trim()
      };

      const response = await POST(`it-dept/tickets/${selectedTicketForComments.ticketId}/comment`, payload);
      
      if (response?.data?.statusCode === 201) {
        const updatedTicket = response.data.data;
        const parsedComments = parseComments(updatedTicket.commentsJson);
        setComments(parsedComments);
        setNewComment("");
        setSelectedTicketForComments(updatedTicket);
        // Update unread count - get read comments from localStorage
        const readCommentsKey = `readComments_${selectedTicketForComments.ticketId}_${empId}`;
        const readComments = JSON.parse(localStorage.getItem(readCommentsKey) || '[]');
        const unreadCount = parsedComments.filter(c => 
          String(c.senderId) !== String(empId) && 
          !readComments.includes(c.timestamp)
        ).length;
        setUnreadCommentCount(unreadCount);
        if (parsedComments.length > 0) {
          setLastReadCommentId(parsedComments[parsedComments.length - 1].timestamp);
        }
        setSnackbar({
          open: true,
          message: "Comment added successfully",
          severity: "success"
        });
        // Refresh tickets list
        const res = await GET(`it-dept/tickets?empId=${empId}`);
        if (res?.data?.statusCode === 200) {
          const list = res?.data?.data?.paginatedData || [];
          const sorted = (Array.isArray(list) ? list : []).slice().sort((a,b) => {
            const ad = a?.createdOn ? new Date(a.createdOn).getTime() : 0;
            const bd = b?.createdOn ? new Date(b.createdOn).getTime() : 0;
            return bd - ad;
          });
          setTickets(sorted);
        }
      } else {
        setSnackbar({
          open: true,
          message: response?.data?.message || "Failed to add comment",
          severity: "error"
        });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Failed to add comment",
        severity: "error"
      });
    } finally {
      setCommentLoading(false);
    }
  };

  const getSenderName = async (senderId, ticket) => {
    if (String(senderId) === String(empId)) {
      return empName || `Employee ${senderId}`;
    }
    // Check if it's the ticket creator
    if (ticket && String(senderId) === String(ticket.empId)) {
      return ticket.empName || `Employee ${senderId}`;
    }
    // Check if it's the assigned IT admin
    if (ticket && String(senderId) === String(ticket.assignedToEmpId)) {
      return ticket.assignedToEmpName || `IT Admin ${senderId}`;
    }
    // Check cache
    if (employeeNamesCache[senderId]) {
      return employeeNamesCache[senderId];
    }
    // Try to fetch employee name
    try {
      const response = await GET(`employee/employee-details/${senderId}`);
      if (response?.data?.statusCode === 200) {
        const employee = response.data.data?.employee;
        const name = employee?.employeeName || employee?.empName || 
                    `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim() ||
                    `Employee ${senderId}`;
        setEmployeeNamesCache(prev => ({ ...prev, [senderId]: name }));
        return name;
      }
    } catch (err) {
      console.error("Error fetching employee name:", err);
    }
    return `User ${senderId}`;
  };

  // Helper to get sender name synchronously (for display)
  const getSenderNameSync = (senderId, ticket) => {
    if (String(senderId) === String(empId)) {
      return empName || `Employee ${senderId}`;
    }
    if (ticket && String(senderId) === String(ticket.empId)) {
      return ticket.empName || `Employee ${senderId}`;
    }
    if (ticket && String(senderId) === String(ticket.assignedToEmpId)) {
      return ticket.assignedToEmpName || `IT Admin ${senderId}`;
    }
    if (employeeNamesCache[senderId]) {
      return employeeNamesCache[senderId];
    }
    // Fetch in background
    getSenderName(senderId, ticket);
    return `User ${senderId}`;
  };

  const calculateCommentCounts = (ticket, currentEmpId) => {
    if (!ticket || !ticket.commentsJson) {
      return { total: 0, unread: 0 };
    }
    const parsedComments = parseComments(ticket.commentsJson);
    const total = parsedComments.length;
    const readCommentsKey = `readComments_${ticket.ticketId}_${currentEmpId}`;
    const readComments = JSON.parse(localStorage.getItem(readCommentsKey) || '[]');
    const unread = parsedComments.filter(c => 
      String(c.senderId) !== String(currentEmpId) && 
      !readComments.includes(c.timestamp)
    ).length;
    return { total, unread };
  };

  const sortedComments = [...comments].sort((a, b) => {
    const aTime = new Date(a.timestamp).getTime();
    const bTime = new Date(b.timestamp).getTime();
    return sortOrder === "newest" ? bTime - aTime : aTime - bTime;
  });

  const columns = [
    // { field: "rowNumber", headerName: "#", width: 50, sortable: false, filterable: false },
    { field: "assetId", headerName: (
      <>
        Asset <br /> ID
      </>
    ), width: 60, sortable: true, filterable: true },
    { field: "assetNo", headerName: "Asset No", width: 110 },
    {
      field: "componentName",
      headerName: "Component",
      width: 100,
      renderCell: (params) => params.value || "—",
    },
    { field: "assetName", headerName: "Asset Main Description", minWidth: 200 },
    { field: "assignedBy", headerName: "Assigned By", width: 115 },
    {
      field: "assignedDate",
      headerName: "Assigned Date",
      width: 120,
      renderCell: (params) => {
        const date = params.value;
        return date ? dayjs(date).format("DD MMM YYYY") : "—";
      },
    },
    {
      field: "returnTo",
      headerName: (
        <>
        Returned <br /> To
        </>
      ),
      width: 80,
      renderCell: (params) => params.value || "—",
    },
    {
      field: "returnedDate",
      headerName: (
        <>
        Returned <br /> Date
        </>
      ),
      width: 100,
      renderCell: (params) => {
        const date = params.value;
        return date ? dayjs(date).format("DD MMM YYYY") : "—";
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 95,
      renderCell: (params) => {
        const isReturned = !!params.row.returnedDate;
        return (
          <Chip
            label={isReturned ? "Returned" : "Assigned"}
            color={isReturned ? "default" : "success"}
            size="small"
            sx={{
              fontWeight: "bold",
              backgroundColor: isReturned ? "#e0e0e0" : "#4caf50",
              color: isReturned ? "#000" : "#fff",
            }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: (
        <>
          Raise <br /> Ticket
        </>
      ),
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const asset = assets.find(
          (a) =>
            a.workstationId === params.row.workstationId ||
            a.workstationId === params.row.id
        );
        if (!asset) return null;
        const isReturned = !!params.row.returnedDate;
        if (isReturned) return null; // Don't show Raise Ticket for returned assets
        return (
          <Tooltip title="Raise Ticket">
            <IconButton
              onClick={() => handleOpenTicketDialog(asset)}
              color="primary"
              size="small"
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.1)",
                },
              }}
            >
              <MoreVertIcon style={{ marginRight: "0px", width: "" }} />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  const rows = assets.map((asset, index) => ({
    // rowNumber: index + 1,
    id: asset.workstationId,
    assetId: asset.assetId || "—",
    assetNo: asset.assetNo || "—",
    componentName: asset.componentName || "—", 
    assetName: asset.assetName || "—",
    assignedBy: asset.assignedBy || "—",
    assignedDate: asset.assignedDate || null,
    returnedDate: asset.returnedDate || null,
    returnTo: asset.returnTo || "—",
    workstationId: asset.workstationId,
  }));

  const handleReopenTicket = (ticket) => {
    setReopenConfirm({ open: true, ticket });
    setReopenReason("");
  };

  const confirmReopen = async () => {
    if (!reopenConfirm.ticket) return;
    if (!reopenReason.trim()) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Please enter a reopen reason"
      });
      return;
    }
    
    const ticketId = reopenConfirm.ticket.ticketId;
    setReopenConfirm({ open: false, ticket: null });
    
    try {
      const response = await PUT(`it-dept/tickets/${ticketId}/reopen`, {
        reopenReason: reopenReason.trim()
      });
      
      if (response?.data?.statusCode === 200) {
        setReopenReason("");
        setSnackbar({
          open: true,
          severity: "success",
          message: response.data.message || "Ticket reopened successfully"
        });
        // Refresh tickets
        const res = await GET(`it-dept/tickets?empId=${empId}`);
        if (res?.data?.statusCode === 200) {
          const list = res?.data?.data?.paginatedData || [];
          const sorted = (Array.isArray(list) ? list : []).slice().sort((a,b) => {
            const ad = a?.createdOn ? new Date(a.createdOn).getTime() : 0;
            const bd = b?.createdOn ? new Date(b.createdOn).getTime() : 0;
            return bd - ad;
          });
          setTickets(sorted);
          // Update asset tickets if dialog is open
          if (selectedAsset) {
            const assetId = selectedAsset?.assetId;
            const filtered = sorted.filter(t => String(t.empId) === String(empId) && (String(t.assetId) === String(assetId)));
            setAssetTickets(filtered);
          }
        }
      } else {
        setSnackbar({
          open: true,
          severity: "error",
          message: response?.data?.message || "Failed to reopen ticket"
        });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        severity: "error",
        message: err?.response?.data?.message || "Failed to reopen ticket"
      });
    }
  };


  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox p={isMobile ? 1 : 0.5}>
        <Card>
          <CardContent>
            <MDTypography
              variant="h3"
              gutterBottom
              sx={{
                color: "textPrimary",
                fontFamily: properties.fontFamily,
                fontWeight: "bold",
              }}
              fontSize={isMobile ? "1.2rem" : "16px"}
            >
              Assets History
            </MDTypography>

            {loading ? (
              <MDBox display="flex" justifyContent="center" py={10}>
                <Alert severity="info">Loading assets...</Alert>
              </MDBox>
            ) : error ? (
              <MDBox display="flex" justifyContent="center" py={5}>
                <Alert severity="error">{error}</Alert>
              </MDBox>
            ) : assets.length === 0 ? (
              <MDBox display="flex" justifyContent="center" py={5}>
                <Alert severity="info">No assets found.</Alert>
              </MDBox>
            ) : (
              <MDBox style={{ width: "100%", overflowX: "auto" }}>
                <MDBox style={{ minWidth: isMobile ? 500 : "100%" }}>
                  <Paper>
                    <DataGrid
                      rows={rows}
                      columns={columns}
                      pageSize={5}
                      rowsPerPageOptions={[5]}
                      disableSelectionOnClick
                      hideFooter
                      sx={{
                        fontFamily: properties.fontFamily,
                        fontSize: isMobile ? "0.85rem" : "14px",
                        height: "500px",
                        fontWeight: 400,
                        mt: 1,
                        "& .MuiDataGrid-columnHeaders": {
                          backgroundColor: "#f5f5f5",
                          fontWeight: "bold",
                        },
                        "& .MuiDataGrid-row:nth-of-type(odd)": {
                          backgroundColor: "#f9f9f9",
                        },
                        "& .MuiDataGrid-scrollbar": {
                          width: "10px !important",
                        },
                      }}
                    />
                  </Paper>
                </MDBox>
              </MDBox>
            )}
          </CardContent>
        </Card>

        {/* Raise Ticket Dialog */}
        <Dialog
          open={ticketDialogOpen}
          onClose={handleCloseTicketDialog}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              fontFamily: properties.fontFamily,
              minHeight: '500px',
              maxHeight: 'none',
              overflow: 'visible',
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pb: 1,
            }}
          >
            <MDTypography
              variant="h6"
              sx={{ fontFamily: properties.fontFamily, fontSize: "16px" }}
            >
              Raise IT Ticket
            </MDTypography>
            <IconButton edge="end" onClick={handleCloseTicketDialog} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ overflowY: 'visible', overflow: 'visible' }}>
            {selectedAsset && (
              <MDBox sx={{ pt: 0 }}>
                <Tabs 
                  value={dialogTab} 
                  onChange={(e, v) => setDialogTab(v)} 
                  variant="fullWidth" 
                  sx={{ 
                    mb: 3,
                    borderBottom: '2px solid',
                    borderColor: 'divider',
                    gap: 1,
                    '& .MuiTabs-flexContainer': {
                      gap: 1
                    },
                    '& .MuiTabs-indicator': {
                      height: 4,
                      borderRadius: '4px 4px 0 0',
                      backgroundColor: 'primary.main'
                    }
                  }}
                >
                  <Tab 
                    icon={<AddCircleOutlineIcon sx={{ fontSize: 28 }} />}
                    iconPosition="top"
                    label={
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Typography sx={{ fontSize: '15px', fontWeight: 600, fontFamily: properties.fontFamily }}>
                          Raise New Ticket
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: 'text.secondary', fontFamily: properties.fontFamily }}>
                          Create new issue
                        </Typography>
                      </Box>
                    }
                    sx={{
                      textTransform: 'none',
                      minHeight: 100,
                      py: 2,
                      px: 2,
                      fontFamily: properties.fontFamily,
                      borderBottom: dialogTab === 0 ? '4px solid' : 'none',
                      borderColor: dialogTab === 0 ? 'primary.main' : 'transparent',
                      backgroundColor: dialogTab === 0 ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                      borderRadius: '8px 8px 0 0',
                      transition: 'all 0.3s ease',
                      marginRight: '8px',
                      '&.Mui-selected': {
                        color: 'primary.main',
                        fontWeight: 700,
                        backgroundColor: 'rgba(25, 118, 210, 0.08)'
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.06)'
                      }
                    }}
                  />
                  <Tab 
                    icon={<HistoryIcon sx={{ fontSize: 28 }} />}
                    iconPosition="top"
                    label={
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Typography sx={{ fontSize: '15px', fontWeight: 600, fontFamily: properties.fontFamily }}>
                          Existing Tickets
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: 'text.secondary', fontFamily: properties.fontFamily }}>
                          {assetTickets.length} ticket{assetTickets.length !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      textTransform: 'none',
                      minHeight: 100,
                      py: 2,
                      px: 2,
                      fontFamily: properties.fontFamily,
                      borderBottom: dialogTab === 1 ? '4px solid' : 'none',
                      borderColor: dialogTab === 1 ? 'primary.main' : 'transparent',
                      backgroundColor: dialogTab === 1 ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                      borderRadius: '8px 8px 0 0',
                      transition: 'all 0.3s ease',
                      marginLeft: '8px',
                      '&.Mui-selected': {
                        color: 'primary.main',
                        fontWeight: 700,
                        backgroundColor: 'rgba(25, 118, 210, 0.08)'
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.06)'
                      }
                    }}
                  />
                </Tabs>

                {dialogTab === 1 && (
                  <MDBox>
                    {assetTickets.length === 0 ? (
                      <Alert severity="info">No previous tickets for this asset.</Alert>
                    ) : (
                      <MDBox sx={{ maxHeight: '60vh', overflowY: 'auto', overflowX: 'hidden', pr: 1 }}>
                        <Stack spacing={2}>
                          {assetTickets.map((ticket) => {
                            const isClosed = ["DONE", "RESOLVED", "CLOSED"].includes(String(ticket.status || "").toUpperCase());
                            const updatedTime = ticket.lastModifiedOn || ticket.updatedOn || ticket.resolvedOn;
                            const statusColor = ticket.status === "OPEN" ? "default" :
                                              ticket.status === "IN_PROGRESS" ? "primary" : "success";
                            return (
                              <Card 
                                key={`${ticket.ticketId}-${commentsReadRefresh}`} 
                                variant="outlined" 
                                sx={{ 
                                  borderRadius: 2,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                  transition: 'box-shadow 0.2s',
                                  '&:hover': {
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                                  }
                                }}
                              >
                                <CardContent sx={{ pb: 2, pt: 2 }}>
                                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
                                    <Box sx={{ flex: 1 }}>
                                      <Typography sx={{ fontWeight: 600, fontSize: 15, mb: 0.5, color: 'text.primary' }}>
                                        #{ticket.ticketId} • {ticket.title}
                                      </Typography>
                                      <Stack spacing={0.5} sx={{ mt: 1 }}>
                                        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                                          Created on: {ticket.createdOn ? dayjs(ticket.createdOn).format("DD MMM YYYY HH:mm") : "—"}
                                        </Typography>
                                        {updatedTime && (
                                          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                                            Updated on: {dayjs(updatedTime).format("DD MMM YYYY HH:mm")}
                                          </Typography>
                                        )}
                                      </Stack>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, ml: 2 }}>
                                      <Chip
                                        size="small"
                                        label={ticket.status}
                                        color={statusColor}
                                        sx={{ 
                                          fontSize: 11,
                                          fontWeight: 500,
                                          minWidth: 80
                                        }}
                                      />
                                      <Tooltip title={`Priority: ${ticket.priority}`} arrow>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          {ticket.priority === "HIGH" ? (
                                            <ArrowUpwardIcon sx={{ fontSize: 20, color: "error.main" }} />
                                          ) : ticket.priority === "MEDIUM" ? (
                                            <RemoveIcon sx={{ fontSize: 20, color: "warning.main" }} />
                                          ) : (
                                            <ArrowDownwardIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                                          )}
                                        </Box>
                                      </Tooltip>
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.5 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                      {(() => {
                                        const commentCounts = calculateCommentCounts(ticket, empId);
                                        return (
                                          <Tooltip title={commentCounts.unread > 0 ? `${commentCounts.unread} unread comment${commentCounts.unread !== 1 ? 's' : ''}` : commentCounts.total > 0 ? `${commentCounts.total} comment${commentCounts.total !== 1 ? 's' : ''} (all read)` : "View comments"}>
                                            <IconButton 
                                              size="small" 
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenComments(ticket);
                                              }}
                                              sx={{ 
                                                p: 0.5,
                                                color: commentCounts.unread > 0 ? "error.main" : "text.secondary"
                                              }}
                                            >
                                              <Badge 
                                                badgeContent={commentCounts.unread > 0 ? commentCounts.unread : (commentCounts.total > 0 ? commentCounts.total : 0)} 
                                                color={commentCounts.unread > 0 ? "error" : "default"}
                                                sx={{
                                                  '& .MuiBadge-badge': {
                                                    fontSize: '10px',
                                                    height: '18px',
                                                    minWidth: '18px',
                                                    fontWeight: 600
                                                  }
                                                }}
                                              >
                                                <CommentIcon sx={{ fontSize: 18 }} />
                                              </Badge>
                                            </IconButton>
                                          </Tooltip>
                                        );
                                      })()}
                                    </Box>
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleViewTicketDetails(ticket)}
                                        sx={{ 
                                          textTransform: "none",
                                          fontWeight: 500,
                                          px: 2,
                                          borderColor: '#1976d2',
                                          color: '#1976d2',
                                          '&:hover': {
                                            borderColor: '#1565c0',
                                            backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                          }
                                        }}
                                      >
                                        View Details
                                      </Button>
                                      {isClosed && (
                                        <Button
                                          size="small"
                                          variant="contained"
                                          onClick={() => handleReopenTicket(ticket)}
                                          sx={{
                                            backgroundColor: '#ff9800',
                                            color: '#fff',
                                            textTransform: 'none',
                                            '&:hover, &:focus, &:active': {
                                              backgroundColor: '#f57c00',
                                            },
                                          }}
                                        >
                                          Reopen Ticket
                                        </Button>
                                      )}
                                    </Box>
                                  </Box>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </Stack>
                      </MDBox>
                    )}
                  </MDBox>
                )}

                {dialogTab === 0 && (
                  <MDBox sx={{ display: "flex", flexDirection: "column", gap: 2.5, overflowY: 'visible', overflow: 'visible' }}>
                    <Box sx={{ display: "flex", gap: 3, mb: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5, fontFamily: properties.fontFamily }}>
                          Asset Name
                        </Typography>
                        <Typography sx={{ fontSize: '14px', color: 'text.primary', fontWeight: 500, fontFamily: properties.fontFamily }}>
                          {selectedAsset.assetName || "—"}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5, fontFamily: properties.fontFamily }}>
                          Asset No.
                        </Typography>
                        <Typography sx={{ fontSize: '14px', color: 'text.primary', fontWeight: 500, fontFamily: properties.fontFamily }}>
                          {selectedAsset.assetNo || "—"}
                        </Typography>
                      </Box>
                    </Box>
                    <TextField
                      label={
                        <span>
                          Title <span style={{ color: '#d32f2f' }}>*</span>
                        </span>
                      }
                      value={ticketFormData.title}
                      onChange={(e) => handleTicketFormChange("title", e.target.value)}
                      fullWidth
                      variant="outlined"
                      required
                      size="small"
                      placeholder="e.g., Laptop not booting properly"
                      error={false}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: 'divider'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'primary.main',
                            borderWidth: 1
                          },
                          '& fieldset': {
                            borderColor: 'divider'
                          },
                          '&.Mui-error fieldset': {
                            borderColor: 'divider'
                          }
                        },
                        '& .MuiFormLabel-asterisk': {
                          color: '#d32f2f'
                        }
                      }}
                    />
                    <TextField
                      label={
                        <span>
                          Issue Description <span style={{ color: '#d32f2f' }}>*</span>
                        </span>
                      }
                      value={ticketFormData.issueDescription}
                      onChange={(e) =>
                        handleTicketFormChange("issueDescription", e.target.value)
                      }
                      fullWidth
                      variant="outlined"
                      required
                      multiline
                      rows={5}
                      size="small"
                      placeholder="Describe the issue in detail..."
                      error={false}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: 'divider'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'primary.main',
                            borderWidth: 1
                          },
                          '& fieldset': {
                            borderColor: 'divider'
                          },
                          '&.Mui-error fieldset': {
                            borderColor: 'divider'
                          }
                        },
                        '& .MuiFormLabel-asterisk': {
                          color: '#d32f2f'
                        }
                      }}
                    />
                    <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                      <FormControl sx={{ flex: 1 }} size="small" required>
                        <InputLabel required sx={{ '& .MuiInputLabel-asterisk': { color: '#d32f2f' } }}>
                          Priority
                        </InputLabel>
                        <Select
                          value={ticketFormData.priority}
                          onChange={(e) => handleTicketFormChange("priority", e.target.value)}
                          label="Priority"
                          required
                          sx={{
                            height:40,
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'divider'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'divider'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'primary.main',
                              borderWidth: 1
                            },
                            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'divider'
                            }
                          }}
                        >
                          <MenuItem value="LOW">LOW</MenuItem>
                          <MenuItem value="MEDIUM">MEDIUM</MenuItem>
                          <MenuItem value="HIGH">HIGH</MenuItem>
                        </Select>
                      </FormControl>
                      <MDBox sx={{ flex: 1 }}>
                        <input
                          accept="image/png,image/jpeg,image/jpg,image/gif,image/bmp,image/webp,image/svg+xml,image/tiff,image/x-icon"
                          style={{ display: "none" }}
                          id="raised-button-file"
                          type="file"
                          onChange={handleImageChange}
                        />
                        <label htmlFor="raised-button-file">
                          <Button
                            variant="outlined"
                            component="span"
                            size="medium"
                            fullWidth
                            sx={{ 
                              textTransform: "none",
                              py: 1.5,
                              borderStyle: 'dashed',
                              borderWidth: 2,
                              borderColor: 'divider',
                              color: 'text.secondary',
                              backgroundColor: 'action.hover',
                              '&:hover': {
                                borderColor: 'primary.main',
                                backgroundColor: 'action.selected',
                                borderStyle: 'solid'
                              }
                            }}
                          >
                            {ticketFormData.image
                              ? `✓ ${ticketFormData.image.name}`
                              : "📎 Upload Image (Optional)"}
                          </Button>
                        </label>
                        {ticketFormData.image && (
                          <MDTypography
                            variant="caption"
                            sx={{
                              display: "block",
                              mt: 1,
                              color: "text.secondary",
                              fontFamily: properties.fontFamily,
                              fontSize: '0.75rem'
                            }}
                          >
                            File: {ticketFormData.image.name} ({(ticketFormData.image.size / 1024).toFixed(2)} KB)
                          </MDTypography>
                        )}
                      </MDBox>
                    </Box>
                  </MDBox>
                )}
              </MDBox>
            )}
          </DialogContent>
          {dialogTab === 0 && (
            <DialogActions sx={{ p: 3, pt: 2, gap: 1.5 }}>
              <Button
                onClick={handleCloseTicketDialog}
                variant="outlined"
                size="medium"
                disabled={ticketLoading}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  borderColor: 'divider',
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: 'text.secondary',
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitTicket}
                variant="contained"
                size="medium"
                disabled={ticketLoading || !ticketFormData.title.trim() || !ticketFormData.issueDescription.trim() || !ticketFormData.priority}
                startIcon={ticketLoading ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  px: 4,
                  py: 1,
                  backgroundColor: '#1976d2',
                  color: "#fff",
                  '&:hover': {
                    backgroundColor: '#1565c0',
                    color: "#fff",
                  },
                  '&:disabled': {
                    backgroundColor: 'action.disabledBackground',
                    color: 'action.disabled'
                  }
                }}
              >
                {ticketLoading ? "Submitting..." : "Submit Ticket"}
              </Button>
            </DialogActions>
          )}
        </Dialog>

        {/* Reopen confirmation */}
        <Dialog open={reopenConfirm.open} onClose={() => {setReopenConfirm({ open: false, ticket: null }); setReopenReason("");}} fullWidth maxWidth="sm">
          <DialogTitle>Reopen Ticket</DialogTitle>
          <DialogContent dividers>
            {reopenConfirm.ticket && (
              <Stack spacing={2} sx={{mt: 1}}>
                <DialogContentText>
                  Are you sure you want to reopen ticket #{reopenConfirm.ticket?.ticketId} &quot;{reopenConfirm.ticket?.title}&quot;? The Ticket status will change to Open, and the IT admin will be notified for further action.
                </DialogContentText>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Reopen Reason"
                  value={reopenReason}
                  onChange={e => setReopenReason(e.target.value)}
                  placeholder="Enter reason for reopening this ticket (e.g., Issue not fully resolved, still facing the same problem.)"
                  required
                />
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {setReopenConfirm({ open: false, ticket: null }); setReopenReason("");}}>Cancel</Button>
            <Button variant="contained" sx={{ color: "#fff" }} onClick={confirmReopen} disabled={!reopenReason.trim()}>Reopen</Button>
          </DialogActions>
        </Dialog>

        {/* Ticket Details Dialog with Comments */}
        <Dialog
          open={ticketDetailsOpen}
          onClose={() => {
            setTicketDetailsOpen(false);
            setSelectedTicketForDetails(null);
          }}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              fontFamily: properties.fontFamily,
              maxHeight: '90vh'
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pb: 1,
            }}
          >
            <MDTypography
              variant="h6"
              sx={{ fontFamily: properties.fontFamily, fontSize: "16px" }}
            >
              Ticket #{selectedTicketForDetails?.ticketId} - {selectedTicketForDetails?.title}
            </MDTypography>
            <IconButton edge="end" onClick={() => {
              setTicketDetailsOpen(false);
              setSelectedTicketForDetails(null);
            }} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ overflowY: 'visible', maxHeight: 'none' }}>
            {selectedTicketForDetails && (
              <MDBox sx={{ overflowY: 'visible', maxHeight: 'none', pb: 1 }}>
                    {/* Ticket Information Section */}
                    <MDBox sx={{ mb: 1.5 }}>
                      <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, 
                    gap: 1.2,
                    fontFamily: properties.fontFamily
                  }}>
                    <Box>
                      <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5, fontFamily: properties.fontFamily }}>
                        Ticket ID
                      </Typography>
                      <Typography sx={{ fontSize: '14px', color: 'text.primary', fontWeight: 500, fontFamily: properties.fontFamily }}>
                        #{selectedTicketForDetails.ticketId}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5, fontFamily: properties.fontFamily }}>
                        Status
                      </Typography>
                      <Typography sx={{ fontSize: '14px', color: 'text.primary', fontWeight: 500, fontFamily: properties.fontFamily }}>
                        {selectedTicketForDetails.status || "—"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5, fontFamily: properties.fontFamily }}>
                        Priority
                      </Typography>
                      <Typography sx={{ fontSize: '14px', color: 'text.primary', fontWeight: 500, fontFamily: properties.fontFamily }}>
                        {selectedTicketForDetails.priority || "—"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5, fontFamily: properties.fontFamily }}>
                        Asset ID
                      </Typography>
                      <Typography sx={{ fontSize: '14px', color: 'text.primary', fontWeight: 500, fontFamily: properties.fontFamily }}>
                        {selectedTicketForDetails.assetId || "—"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5, fontFamily: properties.fontFamily }}>
                        Created On
                      </Typography>
                      <Typography sx={{ fontSize: '14px', color: 'text.primary', fontWeight: 500, fontFamily: properties.fontFamily }}>
                        {selectedTicketForDetails.createdOn ? dayjs(selectedTicketForDetails.createdOn).format("DD MMM YYYY HH:mm") : "—"}
                      </Typography>
                    </Box>
                    {(selectedTicketForDetails.lastModifiedOn || selectedTicketForDetails.updatedOn) && (
                      <Box>
                        <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5, fontFamily: properties.fontFamily }}>
                          Updated On
                        </Typography>
                        <Typography sx={{ fontSize: '14px', color: 'text.primary', fontWeight: 500, fontFamily: properties.fontFamily }}>
                          {dayjs(selectedTicketForDetails.lastModifiedOn || selectedTicketForDetails.updatedOn).format("DD MMM YYYY HH:mm")}
                        </Typography>
                      </Box>
                    )}
                    {selectedTicketForDetails.resolvedOn && (
                      <Box>
                        <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5, fontFamily: properties.fontFamily }}>
                          Resolved On
                        </Typography>
                        <Typography sx={{ fontSize: '14px', color: 'text.primary', fontWeight: 500, fontFamily: properties.fontFamily }}>
                          {dayjs(selectedTicketForDetails.resolvedOn).format("DD MMM YYYY HH:mm")}
                        </Typography>
                      </Box>
                    )}
                    {selectedTicketForDetails.assignedToEmpName && (
                      <Box>
                        <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5, fontFamily: properties.fontFamily }}>
                          Assigned To
                        </Typography>
                        <Typography sx={{ fontSize: '14px', color: 'text.primary', fontWeight: 500, fontFamily: properties.fontFamily }}>
                          {selectedTicketForDetails.assignedToEmpName} {selectedTicketForDetails.assignedToEmpId ? `(#${selectedTicketForDetails.assignedToEmpId})` : ""}
                        </Typography>
                      </Box>
                    )}
                      </Box>
                    </MDBox>

                    <Divider sx={{ my: 1.5 }} />

                    {/* Description Section */}
                    <MDBox sx={{ mb: 1.5 }}>
                      <MDTypography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: '16px', fontFamily: properties.fontFamily }}>
                        Description
                      </MDTypography>
                      <Box component="ol" sx={{ pl: 2.5, m: 0 }}>
                        {selectedTicketForDetails.issueDescription?.split('\n').map((line, idx) => (
                          line.trim() && (
                            <Typography key={idx} component="li" sx={{ mb: 0.5, fontSize: '14px', color: 'text.primary', fontFamily: properties.fontFamily }}>
                              {line.trim()}
                            </Typography>
                          )
                        )) || (
                          <Typography component="li" sx={{ fontSize: '14px', color: 'text.secondary', fontFamily: properties.fontFamily }}>
                            {selectedTicketForDetails.issueDescription || "No description provided"}
                          </Typography>
                        )}
                      </Box>
                      {selectedTicketForDetails.issueImage && selectedTicketForDetails.imageType && (
                        <Box sx={{mt: 2, display: 'flex', justifyContent: 'flex-start'}}>
                          <Box
                            component="div"
                            sx={{
                              maxWidth: "300px",
                              maxHeight: "200px",
                              borderRadius: 2,
                              overflow: 'hidden',
                              border: '1px solid',
                              borderColor: 'divider',
                              cursor: "pointer",
                              transition: 'transform 0.2s',
                              '&:hover': {
                                transform: 'scale(1.02)',
                                boxShadow: 2
                              }
                            }}
                          >
                            <img
                              alt="Issue"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                display: "block"
                              }}
                              src={`data:${selectedTicketForDetails.imageType};base64,${selectedTicketForDetails.issueImage}`}
                              onClick={(e) => {
                                // Open full size in new window on click
                                const newWindow = window.open();
                                if (newWindow) {
                                  newWindow.document.write(`
                                    <html>
                                      <head><title>Issue Image</title></head>
                                      <body style="margin:0; display:flex; justify-content:center; align-items:center; min-height:100vh; background:#f5f5f5;">
                                        <img src="${e.target.src}" style="max-width:90vw; max-height:90vh; border-radius:8px;" />
                                      </body>
                                    </html>
                                  `);
                                }
                              }}
                            />
                          </Box>
                        </Box>
                      )}
                    </MDBox>
              </MDBox>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 2 }}>
            <Button
              onClick={() => {
                setTicketDetailsOpen(false);
                setSelectedTicketForDetails(null);
              }}
              variant="outlined"
              sx={{ 
                textTransform: "none",
                px: 3,
                py: 0.75,
                borderColor: '#d0d0d0',
                color: '#424242',
                fontWeight: 500,
                ml: 'auto',
                '&:hover': {
                  borderColor: '#9e9e9e',
                  bgcolor: '#f5f5f5'
                }
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Comments Dialog */}
        <Dialog
          open={commentsDialogOpen}
          onClose={() => {
            setCommentsDialogOpen(false);
            setSelectedTicketForComments(null);
            setComments([]);
            setNewComment("");
          }}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              fontFamily: properties.fontFamily,
              maxHeight: '90vh'
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pb: 1,
            }}
          >
            <MDTypography
              variant="h6"
              sx={{ fontFamily: properties.fontFamily, fontSize: "16px" }}
            >
              Comments - Ticket #{selectedTicketForComments?.ticketId} • {selectedTicketForComments?.title}
            </MDTypography>
            <IconButton edge="end" onClick={() => {
              setCommentsDialogOpen(false);
              setSelectedTicketForComments(null);
              setComments([]);
              setNewComment("");
            }} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ overflowY: 'auto', maxHeight: 'calc(90vh - 180px)', '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {selectedTicketForComments && (
              <MDBox sx={{ display: 'flex', flexDirection: 'column', minHeight: '400px' }}>
                {/* Activity Section */}
                <MDBox sx={{ mb: 1.5, flexShrink: 0 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <MDTypography variant="h6" sx={{ fontWeight: 600, fontSize: '16px', fontFamily: properties.fontFamily }}>
                      Activity ({comments.length})
                    </MDTypography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setSortOrder(sortOrder === "newest" ? "oldest" : "newest")}
                      startIcon={<SortIcon fontSize="small" />}
                      sx={{ 
                        textTransform: "none",
                        fontSize: '13px',
                        height: 32,
                        px: 1.5,
                        borderColor: '#d0d0d0',
                        color: '#424242',
                        fontWeight: 500,
                        '&:hover': {
                          borderColor: '#9e9e9e',
                          bgcolor: '#f5f5f5'
                        }
                      }}
                    >
                      {sortOrder === "newest" ? "Newest first" : "Oldest first"}
                    </Button>
                  </Box>
                </MDBox>

                {/* Comments List - Scrollable Area */}
                <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pr: 1, minHeight: 0, pb: 2, '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                  {sortedComments.length === 0 ? (
                    <Alert severity="info" sx={{ borderRadius: 1 }}>No comments yet. Be the first to comment!</Alert>
                  ) : (
                    <Stack spacing={2.5}>
                      {sortedComments.map((comment, idx) => {
                        const senderName = getSenderNameSync(comment.senderId, selectedTicketForComments);
                        const isEdited = comment.edited || false;
                        const readCommentsKey = `readComments_${selectedTicketForComments.ticketId}_${empId}`;
                        const readComments = JSON.parse(localStorage.getItem(readCommentsKey) || '[]');
                        const isUnread = String(comment.senderId) !== String(empId) && !readComments.includes(comment.timestamp);
                        let formattedTime;
                        const timestampStr = comment.timestamp;
                        try {
                          let commentTime;
                          if (typeof timestampStr === 'string') {
                            if (timestampStr.endsWith('Z')) {
                              commentTime = dayjs.utc(timestampStr).local();
                            } else if (timestampStr.includes('T') && timestampStr.match(/[+-]\d{2}:\d{2}$/)) {
                              commentTime = dayjs(timestampStr);
                            } else if (timestampStr.includes('T')) {
                              commentTime = dayjs.utc(timestampStr).local();
                            } else {
                              commentTime = dayjs(timestampStr);
                            }
                          } else {
                            commentTime = dayjs(timestampStr);
                          }
                          
                          if (commentTime.isValid()) {
                            formattedTime = commentTime.format("MMMM D, YYYY [at] h:mm A");
                          } else {
                            const dateObj = new Date(timestampStr);
                            if (!isNaN(dateObj.getTime())) {
                              formattedTime = dayjs(dateObj).format("MMMM D, YYYY [at] h:mm A");
                            } else {
                              formattedTime = timestampStr;
                            }
                          }
                        } catch (e) {
                          console.error("Error parsing timestamp:", e, timestampStr);
                          try {
                            const dateObj = new Date(timestampStr);
                            if (!isNaN(dateObj.getTime())) {
                              formattedTime = dayjs(dateObj).format("MMMM D, YYYY [at] h:mm A");
                            } else {
                              formattedTime = timestampStr;
                            }
                          } catch (e2) {
                            formattedTime = timestampStr;
                          }
                        }
                        return (
                          <Box 
                            key={idx} 
                            sx={{ 
                              display: "flex", 
                              gap: 1.5, 
                              alignItems: "flex-start",
                              p: 1.5,
                              borderRadius: 1
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor: getAvatarColor(senderName),
                                fontSize: '13px',
                                fontWeight: 600,
                                flexShrink: 0
                              }}
                            >
                              {getInitials(senderName)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                <Typography sx={{ fontWeight: 600, fontSize: '14px', color: 'text.primary' }}>
                                  {senderName}
                                </Typography>
                                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                                  {formattedTime}
                                </Typography>
                                {isEdited && (
                                  <Chip
                                    label="Edited"
                                    size="small"
                                    sx={{
                                      height: 18,
                                      fontSize: '11px',
                                      bgcolor: '#e8f5e9',
                                      color: '#2e7d32',
                                      fontWeight: 500
                                    }}
                                  />
                                )}
                              </Box>
                              <Typography sx={{ fontSize: '14px', color: 'text.primary', mb: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {comment.content}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              </MDBox>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 2, borderTop: '1px solid', borderColor: 'divider', position: 'sticky', bottom: 0, backgroundColor: 'background.paper', zIndex: 1 }}>
            <MDBox sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", flex: 1, maxWidth: 'calc(100% - 120px)' }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: getAvatarColor(empName),
                  fontSize: '13px',
                  fontWeight: 600,
                  flexShrink: 0
                }}
              >
                {getInitials(empName)}
              </Avatar>
              <Box sx={{ flex: 1, display: 'flex', gap: 1, alignItems: 'flex-start', minWidth: 0 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={1}
                  maxRows={3}
                  placeholder="Add a comment..."
                  value={newComment}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 150) {
                      setNewComment(value);
                    }
                  }}
                  variant="outlined"
                  size="small"
                  inputProps={{ maxLength: 150 }}
                  helperText={`${newComment.length}/150 characters`}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '14px',
                      py: 0.5,
                      minHeight: '40px',
                      '& textarea': {
                        py: 1,
                        lineHeight: 1.5
                      },
                      '&:hover fieldset': {
                        borderColor: '#1976d2'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2',
                        borderWidth: 2
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      fontSize: '11px',
                      mt: 0.5,
                      ml: 0.5
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (newComment.trim() && !commentLoading) {
                        handleAddComment();
                      }
                    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                      if (newComment.trim() && !commentLoading) {
                        handleAddComment();
                      }
                    }
                  }}
                />
                <IconButton
                  onClick={handleAddComment}
                  disabled={!newComment.trim() || commentLoading}
                  sx={{
                    bgcolor: newComment.trim() ? '#1976d2' : 'transparent',
                    color: newComment.trim() ? '#fff' : '#9e9e9e',
                    width: 40,
                    height: 40,
                    mt: 0.5,
                    flexShrink: 0,
                    '&:hover': {
                      bgcolor: newComment.trim() ? '#1565c0' : 'rgba(0,0,0,0.04)',
                    },
                    '&:disabled': {
                      bgcolor: 'transparent',
                      color: '#bdbdbd'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  {commentLoading ? (
                    <CircularProgress size={16} sx={{ color: '#fff' }} />
                  ) : (
                    <SendIcon fontSize="small" />
                  )}
                </IconButton>
              </Box>
            </MDBox>
            <Button
              onClick={() => {
                setCommentsDialogOpen(false);
                setSelectedTicketForComments(null);
                setComments([]);
                setNewComment("");
              }}
              variant="outlined"
              sx={{ 
                textTransform: "none",
                px: 3,
                py: 0.75,
                borderColor: '#d0d0d0',
                color: '#424242',
                fontWeight: 500,
                '&:hover': {
                  borderColor: '#9e9e9e',
                  bgcolor: '#f5f5f5'
                }
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          anchorOrigin={{vertical: "top", horizontal: "right"}}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity === "success" ? "success" : snackbar.severity === "error" ? "error" : "info"}
            variant="filled"
            sx={{width: "100%", color: "#fff", fontFamily: properties.fontFamily}}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </MDBox>
    </DashboardLayout>
  );
};

export default EmployeeAssetsHistory;
